import { AgGridModule } from '@ag-grid-community/angular';
import {
  CellClickedEvent,
  CellEditingStartedEvent,
  CellValueChangedEvent,
  ColDef,
  ColumnApi,
  ColumnState,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowNode,
  ModuleRegistry,
  RowClassParams,
  RowClickedEvent,
} from '@ag-grid-community/core';
// Import AG Grid Enterprise modules for clipboard and range selection
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { AsyncPipe, CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import type { CdwGridColumn, CdwGridSort } from '@cdw-ae/common-data';
import { MaterialLibraryModule } from '@cdw-ae/common-frontend-materials';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwCheckboxModule } from '@cdw-ae/common-ui-components-checkbox';
import { FilterBarModule } from '@cdw-ae/filter-bar-core';
// Import interfaces from external library
import {
  FilterBarField,
  HeadlessSelectedFilterField,
  SelectedFilterField,
  SqlResponse,
} from '@cdw-ae/filter-bar-interfaces';
import { FilterBarSelectionMatchersModule } from '@cdw-ae/filter-bar-selection-matchers';
import { FilterBarTemporalMatchersModule } from '@cdw-ae/filter-bar-temporal-matchers';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { provideComponentStore } from '@ngrx/component-store';
import * as _ from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { CdwSearchGridComponentStore } from '../+component-state/cdw-search-grid-component.store';
// import { ColumnVisibilityButtonComponent } from '../column-visibility-button/column-visibility-button.component';
import { previewColumn } from '../config/cdw-preview-col.constant';
import { selectionColumn } from '../config/cdw-selection-col.constant';
import { defaultCdwColDef } from '../config/default-cdw-col-def.constant';
import { CustomHeaderComponent } from '../custom-header/custom-header.component';
import { DeleteItemIconComponent } from '../delete-item-icon/delete-item-icon.component';
import { GridFooterComponent } from '../grid-footer/grid-footer.component';
import {
  CdwColDef,
  CdwRowClickedEvent,
} from '../interfaces/cdw-col-def.interface';
import { CdwSidebarDef } from '../interfaces/cdw-sidebar-def.interface';
import {
  ApiResponse,
  ICdwSearchGridDataService,
  SearchGridDataSource,
} from '../interfaces/data-source.interface';
// import { ItemsSelectedComponent } from '../items-selected/items-selected.component';
import { MultiSelectHeaderComponent } from '../multi-select-header/multi-select-header.component';
import { PreviewIconComponent } from '../preview-icon/preview-icon.component';
import { SelectAllBannerComponent } from '../select-all-banner/select-all-banner.component';
import { SelectCheckboxComponent } from '../select-checkbox/select-checkbox.component';
import { GridActionNotificationService } from '../services/grid-action-notification.service';

// Register AG Grid Enterprise modules for range selection and clipboard
ModuleRegistry.registerModules([RangeSelectionModule, ClipboardModule]);

@Component({
  selector: 'lib-cdw-search-grid',
  templateUrl: './cdw-search-grid.component.html',
  styleUrls: ['./cdw-search-grid.component.scss'],
  standalone: true,
  imports: [
    AgGridModule,
    CommonModule,
    AsyncPipe,
    MatButtonModule,
    MatIconModule,
    CdwButtonModule,
    CdwCheckboxModule,
    MaterialLibraryModule,
    FilterBarModule,
    FilterBarTextMatchersModule,
    FilterBarSelectionMatchersModule,
    FilterBarTemporalMatchersModule,
    // ColumnVisibilityButtonComponent,
    // DeleteItemIconComponent,
    // PreviewIconComponent,
    GridFooterComponent,
    // MultiSelectHeaderComponent,
    // SelectCheckboxComponent,
    SelectAllBannerComponent,
    // ItemsSelectedComponent,
  ],
  providers: [provideComponentStore(CdwSearchGridComponentStore)],
  host: {
    '[style.--custom-text-color]': 'customTextColor',
    '[style.--first-column-text-color]':
      'firstColumnTextColor || customTextColor',
    '[style.--custom-header-text-color]': 'customHeaderTextColor',
    '[style.--custom-header-font-weight]': 'customHeaderFontWeight',
    '[style.--custom-first-column-font-weight]':
      'customFirstColumnFontWeight || customRowFontWeight',
    '[style.--custom-row-font-weight]': 'customRowFontWeight',
    '[style.--custom-font-family]': 'fontFamily',
    '[style.--custom-header-font-size]': 'customHeaderFontSize',
    '[style.--custom-row-font-size]': 'customRowFontSize',
    '[style.--header-bg-color]': 'headerBgColor',
    '[style.--footer-bg-color]': 'footerBgColor',
  },
})
export class CdwSearchGridComponent<T extends { id: string | number }>
  implements OnInit, OnChanges, OnDestroy
{
  @Output() errorRetrievingData = new EventEmitter<Error>();

  @Output() clearError = new EventEmitter<void>();

  @Output() rowClicked = new EventEmitter<CdwRowClickedEvent<T>>();

  @Output() deleteRowClicked = new EventEmitter<{
    rowId: number | string;
    gridComponentId?: string;
  }>();

  @Output() selectedFilterFieldsOutput = new EventEmitter<
    SelectedFilterField[]
  >();

  @Output() selectedIdsOutput = new EventEmitter<(string | number)[]>();

  @Output() selectedNodesOutput = new EventEmitter<unknown[]>();

  @Output() columnStateOutput = new EventEmitter<ColumnState[]>();

  @Output() sortOrderOutput = new EventEmitter<
    { name: string; direction?: 'ASC' | 'DESC' }[]
  >();

  @Output() filtersChange = new EventEmitter<{
    filters: SelectedFilterField[];
    filterBarId: string;
  }>();

  @Output() saveChangesRequested = new EventEmitter<void>();

  @Output() discardChangesRequested = new EventEmitter<void>();

  @Output() editModeChanged = new EventEmitter<boolean>();

  @Output() dataLoaded = new EventEmitter<void>();

  @Input() cdwSearchGridDataService!: ICdwSearchGridDataService<T>;

  @Input() componentId!: string;

  @Input() selectMode$: Observable<boolean> = of(false);

  @Input() searchGridDataSource: SearchGridDataSource = 'sql';

  @Input() itemLabel = 'item'; // devices | users | classes

  /**
   * Optional callback to fetch all IDs for "select all" functionality
   * When provided, clicking "Select all X items" will call this function
   * to get all IDs matching current filters, then add them to selectedIds
   */
  @Input() fetchAllIds?: () => Observable<string[]>;

  @Input() initialPageSize = 25;

  @Input() initialPage?: number; // Optional initial page to restore pagination state

  @Input() useMatIcon = false; // Pass through to child components that use icons

  @Input() customTextColor?: string;

  @Input() firstColumnTextColor?: string;

  @Input() customHeaderTextColor?: string;

  @Input() customHeaderFontWeight?: string; // Custom font weight for header

  @Input() customFirstColumnFontWeight?: string; // Custom font weight for first column

  @Input() customRowFontWeight?: string; // Custom font weight for rows

  @Input() fontFamily?: string; // Custom font family for all grid text

  @Input() customHeaderFontSize?: string; // Custom font size for headers

  @Input() customRowFontSize?: string; // Custom font size for rows

  @Input() showEditModeButton = false;

  @Input() editableCellColor = '#f0f9ff'; // Default light blue

  @Input() modifiedCellColor = '#fef08a'; // Default yellow for modified cells

  @Input() showCheckboxSelection = false; // Show checkbox selection column

  @Input() showPreviewColumn = false; // Show preview icon column

  @Input() headerBgColor?: string; // Custom background color for column headers

  @Input() footerBgColor?: string; // Custom background color for footer badge

  @Input() previewField?: string; // Field name to display in preview tooltip (e.g., 'lastSync')

  @Input() previewFieldLabel?: string; // Custom label for preview field (e.g., 'Last refreshed')

  @Input() rowHeight?: number; // Custom row height in pixels (default is AG Grid's default)

  @Input() enableColumnReordering = false; // Enable drag-and-drop column reordering (default: false)

  @Input() enableCellClickEditing = false; // Enable entering edit mode by clicking editable cells (default: false)

  @Input() suppressCellClickNavigation = false; // Prevent navigation when clicking cells (default: false)

  @Input() showNonEditableTooltips = false; // Show tooltips on non-editable cells on click (default: false)

  public grey400 = '#505259';
  public cdwRedPrimary = '#E4002B';

  public showSelectedOnly = false; // Filter grid to show only selected rows
  private selectedIdSet = new Set<string>(); // Synchronous mirror of componentState selectedIds for AG Grid external filter

  public isEditMode = false;
  public cellChanges: Map<string, Record<string, unknown>> = new Map();
  public isSaving = false;

  // Track ONLY the current in-cell edit job (null when no job active)
  // Only one in-cell edit job can be active at a time since editing is disabled during job processing
  // Does NOT track bulk update jobs or any other job types
  private activeInCellEditJobId: number | null = null;
  // Track which cells are currently saving (to show loading spinners)
  private savingCells: Set<string> = new Set();

  public gridReady$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  // Click tooltip tracking
  private activeClickTooltip: HTMLElement | null = null;
  private tooltipMouseMoveHandler: ((e: Event) => void) | null = null;
  private tooltipMouseLeaveHandler: (() => void) | null = null;

  // #region filter bar input paramaters and public members
  @Input() filterBarSettings!: FilterBarField[];

  @Input() fieldMask$?: Observable<string[]>;

  @Input() limitOneFilter$?: Observable<boolean>;

  @Input() defaultCdwColDef: CdwColDef = defaultCdwColDef;

  @Input() cdwSidebarDef!: CdwSidebarDef;

  @Input() filterBySelectedColumnName = '';

  @Input() filterBarId = 'default';

  @Input() initialSelectedFilterFields?: SelectedFilterField[];

  public selectedFilterFields$!: Observable<SelectedFilterField[]>;
  // #endregion

  // #region AG Grid input parameters and public members
  @Input() cacheBlockSize = 200;

  @Input() columnDefs!: CdwColDef[];

  @Input() pageSizeSelectorBottomPosition = '13px';

  @Input() noRowsOverlayText = 'No data matches the current filter settings.'; // Custom text for empty grid overlay

  public params!: GridReadyEvent; // ICellRendererParams<T>;

  public gridOptions!: GridOptions;

  public hidePagination = true;
  // #endregion

  // #region NGRX state public observables
  public pageSize$: Observable<number>;

  public filterBySelected$: Observable<boolean>;

  public selectedIdCount$: Observable<number>;

  public selectedCount = 0;

  // #endregion

  // #region AG Grid private members
  public gridApi?: GridApi<T>;

  public columnApi?: ColumnApi;

  private searchError: Error = new Error(
    `An error occurred while searching for ${this.itemLabel}s`
  );

  // #endregion

  // #region private NGRX state
  private initialPageLoad$: Observable<boolean>;

  private initialPageLoad?: boolean;

  private headlessFilterFields$: Observable<HeadlessSelectedFilterField[]>;

  private searchItemTotal$: Observable<number>;

  private currentPage$: Observable<number>;

  private stateLoaded$: Observable<boolean>;

  private nextPageToken$: Observable<string | undefined>;

  private totalItemsLoaded$: Observable<number>;

  private selectedIds$: Observable<(string | number)[]>;

  private columnState$: Observable<ColumnState[]>;

  private sortOrder$: Observable<
    { name: string; direction?: 'ASC' | 'DESC' }[]
  >;

  // #endregion

  private onDestroy$: Subject<void> = new Subject<void>();
  private columnSelectorObserver?: MutationObserver; // Watch for column selector DOM changes

  // @Inject(CDW_SEARCH_GRID_DATA_SERVICE_TOKEN) private cdwSearchGridDataService: ICdwSearchGridDataService<T>

  constructor(
    private readonly componentState: CdwSearchGridComponentStore<T>,
    private gridNotifications: GridActionNotificationService,
    private elementRef: ElementRef
  ) {
    this.componentState.initSearchGrid();
    this.componentState.retrieveComponentState();

    this.selectedFilterFields$ = this.componentState.selectedFilterFields$;
    this.initialPageLoad$ = this.componentState.initialPageLoad$;
    this.pageSize$ = this.componentState.pageSize$;
    this.headlessFilterFields$ = this.componentState.headlessFilterFields$;
    this.searchItemTotal$ = this.componentState.searchItemTotal$;
    this.currentPage$ = this.componentState.currentPage$;
    this.stateLoaded$ = this.componentState.stateLoaded$;
    this.nextPageToken$ = this.componentState.nextPageToken$;
    this.totalItemsLoaded$ = this.componentState.totalItemsLoaded$;
    this.filterBySelected$ = this.componentState.filterBySelected$;
    this.selectedIdCount$ = this.componentState.selectedIdCount$;
    this.selectedIds$ = this.componentState.selectedIds$;
    this.columnState$ = this.componentState.columnState$;
    this.sortOrder$ = this.componentState.sortOrder$;

    this.selectedFilterFields$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedFilterFields: SelectedFilterField[]): void => {
        this.selectedFilterFieldsOutput.emit(selectedFilterFields);
      });

    this.selectedIdCount$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((count: number): void => {
        this.selectedCount = count;
        // Auto-revert to "Show All" when all items are deselected while in "Show Selected Only" mode
        if (count === 0 && this.showSelectedOnly) {
          this.showSelectedOnly = false;
          this.gridApi?.onFilterChanged();
        }
      });

    this.selectedIds$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedIds: (string | number)[]): void => {
        this.selectedIdSet = new Set(selectedIds.map((id) => id.toString()));
        this.selectedIdsOutput.emit(selectedIds);
        this.selectedNodesOutput.emit(
          selectedIds
            .map(
              (id: string | number) =>
                this.gridApi?.getRowNode(id.toString())?.data
            )
            .filter((rowData) => rowData !== undefined)
        );
      });

    this.columnState$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((columnState: ColumnState[]): void => {
        this.columnStateOutput.emit(columnState);
      });

    this.sortOrder$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (sortOrder: { name: string; direction?: 'ASC' | 'DESC' }[]): void => {
          this.sortOrderOutput.emit(sortOrder);
        }
      );

    this.componentState.initialPageLoad$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((initialPageLoad: boolean): void => {
        this.initialPageLoad = initialPageLoad;
      });

    this.pageSize$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((pageSize: number) => {
        if (this.gridApi) {
          this.gridApi.paginationSetPageSize(pageSize);
          this.gridApi.refreshServerSide({ purge: true });
          this.refreshGrid();
        }
      });
  }

  public ngOnDestroy() {
    this.onDestroy$.next();

    // Clean up MutationObserver
    if (this.columnSelectorObserver) {
      this.columnSelectorObserver.disconnect();
    }

    // Clean up injected column selector icons
    const icons = document.querySelectorAll('.cdw-column-selector-icon');
    icons.forEach((icon) => icon.remove());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('cacheBlockSize' in changes) {
      this.gridOptions = this.getGridOptions();
    }

    if ('enableColumnReordering' in changes) {
      // Update the default column definition to respect the enableColumnReordering input
      this.defaultCdwColDef = {
        ...this.defaultCdwColDef,
        suppressMovable: !this.enableColumnReordering,
      };

      // Recreate grid options if grid is already initialized
      if (this.gridApi) {
        this.gridOptions = this.getGridOptions();
      }
    }

    // Apply custom header component to columns with headerIcon
    // Also add headerTooltip for columns with headerName (improves readability when columns are narrow)
    if ('columnDefs' in changes && this.columnDefs) {
      this.columnDefs = this.columnDefs.map((colDef) => {
        const updatedColDef = { ...colDef };

        // Add headerTooltip if headerName exists and headerTooltip is not already set
        // This shows the full header name on hover when columns are too narrow
        if (colDef.headerName && !colDef.headerTooltip) {
          updatedColDef.headerTooltip = colDef.headerName;
        }

        // Apply custom header component for columns with headerIcon.
        // An explicitly-authored headerTooltip moves onto the icon
        // (MatTooltip, anchored below it) and is cleared from the colDef so
        // AG-Grid's mouse-following tooltip doesn't double up (EDUS-2346 QA).
        // The headerName fallback above stays on the cell — it exists to
        // reveal truncated labels, not to explain the icon.
        if ((colDef as CdwColDef).headerIcon) {
          updatedColDef.headerComponent = 'customHeaderComponent';
          updatedColDef.headerComponentParams = {
            icon: (colDef as CdwColDef).headerIcon,
            tooltip: colDef.headerTooltip,
          };
          if (colDef.headerTooltip) {
            updatedColDef.headerTooltip = undefined;
          }
        }

        return updatedColDef;
      });

      // EDUS-2390: Ensure selection and preview columns are present when columnDefs changes
      // The selectMode$ subscription only runs once (cold observable), so we need to
      // re-add these columns here when parent components rebuild columnDefs
      this.ensureLockedColumnsPresent();
    }
  }

  /**
   * Ensures selection and preview columns are present in columnDefs based on input flags.
   * Called from ngOnChanges when columnDefs input changes to handle cases where
   * parent components rebuild columnDefs without these locked columns.
   * (EDUS-2390: Fixes checkbox column disappearing after background state updates)
   */
  private ensureLockedColumnsPresent(): void {
    // Check if selection column should be present
    const hasSelectionColumn = this.columnDefs.some(
      (col) => col.colId === 'selectCheckboxComponent'
    );
    if (this.showCheckboxSelection && !hasSelectionColumn) {
      this.columnDefs.unshift({ ...selectionColumn });
    }

    // Check if preview column should be present (after selection column)
    const hasPreviewColumn = this.columnDefs.some(
      (col) => col.colId === 'previewColumn' || _.isEqual(col, previewColumn)
    );
    if (this.showPreviewColumn && !hasPreviewColumn) {
      // Insert after selection column if present, otherwise at beginning
      const insertIndex = this.showCheckboxSelection ? 1 : 0;
      this.columnDefs.splice(insertIndex, 0, { ...previewColumn });
    }

    // Mark the first data column for styling
    this.markFirstDataColumn();

    // Update grid if already initialized
    if (this.gridApi) {
      this.gridApi.setColumnDefs(this.columnDefs);
    }
  }

  public ngOnInit(): void {
    // Set the editable cell color as a CSS custom property with 0.15 opacity
    const colorWithOpacity = this.hexToRgba(this.editableCellColor, 0.15);
    this.elementRef.nativeElement.style.setProperty(
      '--editable-cell-color',
      colorWithOpacity
    );

    // Set the modified cell color as a CSS custom property with 0.15 opacity
    const modifiedColorWithOpacity = this.hexToRgba(
      this.modifiedCellColor,
      0.15
    );
    this.elementRef.nativeElement.style.setProperty(
      '--modified-cell-color',
      modifiedColorWithOpacity
    );

    // Set the row height as a CSS custom property for AG Grid
    if (this.rowHeight) {
      this.elementRef.nativeElement.style.setProperty(
        '--ag-row-height',
        `${this.rowHeight}px`
      );
    }

    // Note: Column selector icons are now injected when the tool panel is opened
    // See onGridReady() for the event listener setup

    if (!this.filterBarSettings) {
      throw new Error('must provide filter bar fields as an input parameter');
    }
    if (!this.columnDefs) {
      throw new Error('must provide column definitions as an input parameter');
    }

    if (!this.componentId) {
      throw new Error('must provide a componentId');
    }

    if (!this.cdwSearchGridDataService) {
      throw new Error('must provide a cdwSearchGridDataService');
    }

    if (
      this.searchGridDataSource === 'sql' &&
      !this.filterBySelectedColumnName
    ) {
      throw new Error(
        'must provide filterBySelectedColumnName input parameter for sql datasource'
      );
    }

    if (
      this.searchGridDataSource === 'sql' &&
      !this.cdwSearchGridDataService.fetchSqlData
    ) {
      throw new Error(
        'must provide fetchSqlData method in CdwSearchGridDataService for sql datasource'
      );
    }

    if (
      this.searchGridDataSource === 'api' &&
      !this.cdwSearchGridDataService.fetchApiData
    ) {
      throw new Error(
        'must provide fetchSqlData method in CdwSearchGridDataService for sql datasource'
      );
    }

    if (this.initialSelectedFilterFields?.length) {
      this.componentState.setSelectedFilterFields({
        selectedFilterFields: this.initialSelectedFilterFields,
      });
    }

    this.componentState.setPageSize({ pageSize: this.initialPageSize });

    // Set initial page if provided (for pagination restoration)
    if (this.initialPage !== undefined && this.initialPage > 0) {
      this.componentState.setCurrentPage({ currentPage: this.initialPage });
      this.componentState.setInitialPageLoad({ initialPageLoad: true });
    }

    this.componentState.setComponentId({ componentId: this.componentId });

    this.componentState.setSearchGridDataSource({
      searchGridDataSource: this.searchGridDataSource,
    });

    this.selectMode$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectMode: boolean) => {
        // Show selection column if either selectMode is true OR showCheckboxSelection input is true
        const shouldShowSelection = selectMode || this.showCheckboxSelection;

        if (
          shouldShowSelection &&
          !_.isEqual(this.columnDefs[0], selectionColumn)
        ) {
          this.columnDefs.unshift({ ...selectionColumn });
        }
        if (
          !shouldShowSelection &&
          _.isEqual(this.columnDefs[0], selectionColumn)
        ) {
          this.columnDefs.shift();
        }

        // Handle preview column (should be after selection column if both exist)
        const previewColumnIndex = this.columnDefs.findIndex((col) =>
          _.isEqual(col, previewColumn)
        );
        const hasPreviewColumn = previewColumnIndex !== -1;

        if (this.showPreviewColumn && !hasPreviewColumn) {
          // Insert preview column after selection column if it exists, otherwise at the beginning
          const insertIndex = shouldShowSelection ? 1 : 0;
          this.columnDefs.splice(insertIndex, 0, { ...previewColumn });
        }
        if (!this.showPreviewColumn && hasPreviewColumn) {
          this.columnDefs.splice(previewColumnIndex, 1);
        }

        // Mark the first data column (after locked columns) with a CSS class for styling
        this.markFirstDataColumn();

        this.gridOptions = this.getGridOptions();

        if (this.gridApi) {
          this.gridApi.setColumnDefs(this.columnDefs);
        }

        this.gridReady$.next(true);
      });

    this.gridNotifications.deleteRow$
      .pipe(
        filter(({ gridComponentId }) => gridComponentId === this.componentId),
        takeUntil(this.onDestroy$)
      )
      .subscribe(({ rowId, gridComponentId }) => {
        this.deleteRowClicked.emit({ rowId, gridComponentId });
      });
  }

  public refreshGrid() {
    this.componentState.clearSearch();
    this.search();
  }

  public updateRowData(rowId: string, data: T): void {
    if (!this.gridApi) return;
    const rowNode = this.gridApi.getRowNode(rowId);
    if (rowNode) {
      rowNode.setData(data);
    }
  }

  public getCurrentPage(): number {
    return this.gridApi?.paginationGetCurrentPage() ?? 0;
  }

  public onGridReady(params: GridReadyEvent): void {
    this.params = params;
    this.params.api.addEventListener('sortChanged', (): void => {
      this.updateSortOrder();
    });

    // columnVisible or columnMoved, for example
    this.params.api.addEventListener('displayedColumnsChanged', (): void => {
      this.updateColumnState();
    });

    this.gridApi = params.api as GridApi<T>;
    this.columnApi = params.columnApi;

    // Initialize tooltips for non-editable cells when grid is ready
    this.initializeTooltips();

    // Wait for state to be loaded, then apply column state
    this.stateLoaded$
      .pipe(
        filter((loaded) => loaded),
        take(1),
        switchMap(() => this.columnState$.pipe(take(1)))
      )
      .subscribe((state: ColumnState[]) => {
        if (!this.columnApi || state.length === 0) {
          return;
        }

        // Get columns that have lockVisible: true from column definitions
        // Use colId if explicitly set, otherwise fall back to field, cellRenderer, or headerName
        const lockedVisibleColumns = this.columnDefs
          .filter((col) => col.lockVisible === true)
          .map(
            (col) =>
              col.colId || col.field || col.cellRenderer || col.headerName
          );

        // Filter out hide: true from locked visible columns
        const filteredState = state.map((colState) => {
          const isLockedVisible = lockedVisibleColumns.includes(colState.colId);
          if (isLockedVisible && colState.hide) {
            return { ...colState, hide: false };
          }
          return colState;
        });

        // Separate locked columns from other columns
        const lockedColumnStates = filteredState.filter((colState) =>
          lockedVisibleColumns.includes(colState.colId)
        );
        const otherColumnStates = filteredState.filter(
          (colState) => !lockedVisibleColumns.includes(colState.colId)
        );

        // Remove sortIndex from locked columns to preserve their definition order
        const lockedWithoutSort = lockedColumnStates.map((colState) => {
          const { sortIndex: _sortIndex, ...rest } = colState;
          return rest;
        });

        // Sort locked columns by their definition order (Select first, View second)
        const orderedLockedStates = lockedWithoutSort.sort((a, b) => {
          const orderA = a.colId === 'selectCheckboxComponent' ? 0 : 1;
          const orderB = b.colId === 'selectCheckboxComponent' ? 0 : 1;
          return orderA - orderB;
        });

        // Combine: locked columns first (in correct order), then other columns
        const reorderedState = [...orderedLockedStates, ...otherColumnStates];

        this.columnApi.applyColumnState({
          state: reorderedState,
          applyOrder: true,
        });
      });
    this.updateSortOrder();

    this.gridApi.addEventListener('paginationChanged', (): void => {
      if (!this.initialPageLoad && this.gridApi) {
        this.componentState.setCurrentPage({
          currentPage: this.gridApi.paginationGetCurrentPage(),
        });
      }
    });

    // Listen for tool panel visibility changes to inject column selector icons
    this.gridApi.addEventListener('toolPanelVisibleChanged', (): void => {
      this.injectColumnSelectorIcons();
    });

    // Set up MutationObserver to watch for column selector DOM changes
    this.setupColumnSelectorObserver();

    this.pageSize$.pipe(take(1)).subscribe((pageSize: number) => {
      if (this.gridApi) {
        this.gridApi.paginationSetPageSize(pageSize);
      }
      this.search();
    });
  }

  public onFilterBarChange(filterFields: SelectedFilterField[]): void {
    // Update internal state synchronously FIRST to ensure state is ready
    // when parent component's handler executes
    if (this.searchGridDataSource === 'api') {
      this.componentState.clearNextPageToken();
    }
    this.componentState.setSelectedFilterFields({
      selectedFilterFields: filterFields,
    });
    this.componentState.turnOffFilterBySelected();
    this.componentState.turnOffPromptSelectAll();
    this.componentState.turnOffSelectAll();
    this.componentState.clearSearch();

    // Emit filter change event AFTER updating state
    this.filtersChange.emit({
      filters: filterFields,
      filterBarId: this.filterBarId,
    });

    // If parent component is handling filter changes (has filtersChange subscriber),
    // they will call refreshGrid(). Otherwise, automatically trigger search for
    // components that don't use external state management.
    // Check if this event has subscribers - if not, auto-refresh
    if (!this.filtersChange.observed) {
      this.search();
    }
  }

  public onPageSizeChange(pageSize: number): void {
    this.componentState.setPageSize({ pageSize });
  }

  public rowClickedHandler(event: RowClickedEvent<unknown>): void {
    // Disable row click navigation when in edit mode
    if (this.isEditMode) {
      return;
    }

    // Disable row click navigation if the row is updating (bulk action in progress)
    const rowData = event.data as Record<string, unknown>;
    // biome-ignore lint/complexity/useLiteralKeys: bracket notation required by noPropertyAccessFromIndexSignature
    if (rowData?.['isUpdating']) {
      return;
    }

    // Disable row click navigation if any cells in this row are currently saving
    const deviceId = this.getRowId(event.data);
    if (deviceId && this.isRowSaving(deviceId)) {
      return;
    }

    // Only suppress cell click navigation if configured
    if (this.suppressCellClickNavigation) {
      // Check if click originated from a cell - if so, suppress row click navigation
      const clickEvent = event.event as MouseEvent | undefined;
      if (clickEvent) {
        const target = clickEvent.target as HTMLElement;
        // If the click was on a cell (has ag-cell class), suppress row navigation
        if (target?.closest('.ag-cell')) {
          return;
        }
      }
    }

    // Safe: Component generic T constrains data to { id: string | number }
    const row = event.data as T | undefined;
    if (row) {
      // Safe: We're narrowing from unknown to T based on component constraint
      this.rowClicked.emit(event as RowClickedEvent<T>);
    }
  }

  public clickFilterBySelected(): void {
    combineLatest([this.filterBySelected$, this.selectedIdCount$])
      .pipe(take(1))
      .subscribe(
        ([filterBySelected, selectedIdCount]: [boolean, number]): void => {
          if (selectedIdCount === 0) {
            return;
          }
          if (filterBySelected) {
            this.componentState.turnOffFilterBySelected();

            this.search({ clearFilterBySelected: true });
            return;
          }

          this.componentState.turnOnFilterBySelected();
          this.search({ filterBySelected: true });
        }
      );
  }

  public handleClickDeselect(): void {
    this.search({ clearFilterBySelected: true });
  }

  public handleDeselectAll(): void {
    this.componentState.clearSelectedIds();
    this.componentState.updateMultiSelectCheckboxState();
    this.componentState.turnOffFilterBySelected();
    this.componentState.turnOffPromptSelectAll();
    // Save the cleared state to localStorage to prevent selections from being restored
    this.componentState.saveComponentState();
    // Auto-revert to show all when deselecting all
    if (this.showSelectedOnly) {
      this.showSelectedOnly = false;
      this.gridApi?.onFilterChanged();
    }
  }

  /**
   * Toggle between showing all rows and showing only selected rows.
   * Uses AG Grid's external filter API.
   */
  public toggleShowSelectedOnly(show: boolean): void {
    this.showSelectedOnly = show;
    this.gridApi?.onFilterChanged(); // Triggers AG Grid to re-evaluate external filter
  }

  /**
   * AG Grid external filter callback - tells AG Grid whether an external filter is active.
   */
  public isExternalFilterPresent = (): boolean => this.showSelectedOnly;

  /**
   * AG Grid external filter callback - determines if a row passes the filter.
   * Checks against componentState's selectedIdSet (the actual source of truth),
   * not node.isSelected() which is never set by our custom checkbox selection.
   */
  public doesExternalFilterPass = (node: RowNode): boolean => {
    if (!this.showSelectedOnly) return true;
    return this.selectedIdSet.has(node.id ?? '');
  };

  public toggleEditMode(): void {
    if (!this.gridApi) return;

    this.isEditMode = !this.isEditMode;
    this.editModeChanged.emit(this.isEditMode);
    // Use the original columnDefs array to preserve all custom properties
    const columnDefs = this.columnDefs;

    if (columnDefs) {
      columnDefs.forEach((colDef: ColDef) => {
        // Hide/show checkbox selection column in edit mode
        if (colDef.cellRenderer === 'selectCheckboxComponent') {
          colDef.hide = this.isEditMode;
        }

        if (colDef.field) {
          const isEditable = this.isEditableField(colDef.field);

          if (isEditable) {
            colDef.editable = this.isEditMode
              ? () => {
                  // Disable all editing if an in-cell edit job is in progress
                  return !this.hasActiveInCellEditJob();
                }
              : false;

            // Add tooltip for disabled cells during in-cell edit job processing
            colDef.tooltipValueGetter = this.isEditMode
              ? () => {
                  if (this.hasActiveInCellEditJob()) {
                    return 'Cell editing is temporarily disabled while updates are being processed';
                  }
                  return undefined;
                }
              : undefined;
            // Add visual indicator for editable cells - use function to check if modified or saving
            // ALWAYS use a function to preserve saving indicators even when not in edit mode
            colDef.cellClass = this.isEditMode
              ? (params) => {
                  const rowId = params.data?.[this.filterBySelectedColumnName];
                  const field = params.colDef.field;

                  // Check if cell is currently saving
                  if (rowId && field && this.isCellSaving(rowId, field)) {
                    return 'saving-cell';
                  }

                  // Check if cell has been modified
                  if (rowId && field && this.cellChanges.has(String(rowId))) {
                    const changes = this.cellChanges.get(String(rowId));
                    if (changes && changes[field] !== undefined) {
                      return 'modified-cell';
                    }
                  }
                  return 'editable-cell';
                }
              : (params) => {
                  // Not in edit mode - only show saving indicator if cell is saving
                  const rowId = params.data?.[this.filterBySelectedColumnName];
                  const field = params.colDef.field;

                  if (rowId && field && this.isCellSaving(rowId, field)) {
                    return 'saving-cell';
                  }

                  return '';
                };
            // Remove tooltip from editable cells
            colDef.tooltipValueGetter = undefined;
          } else if (this.isEditMode && this.showNonEditableTooltips) {
            // Add tooltip for non-editable cells when in edit mode (only if enabled)
            colDef.tooltipValueGetter = () =>
              'Permission restricted. View only';
          } else if (!this.isEditMode && this.showNonEditableTooltips) {
            // Add tooltip for non-editable cells when NOT in edit mode (only if enabled)
            colDef.tooltipValueGetter = () =>
              'Permission restricted. Click an editable cell to enable edit mode.';
          } else {
            // Remove tooltip when tooltips are disabled
            colDef.tooltipValueGetter = undefined;
          }
        }
      });
      this.gridApi.setColumnDefs(columnDefs);
      // MutationObserver will automatically re-inject icons when DOM updates

      if (this.isEditMode) {
        // Entering edit mode: Show only selected rows
        this.cellChanges.clear();

        // Get selected IDs and fetch only those rows
        this.selectedIds$
          .pipe(take(1))
          .subscribe((selectedIds: (string | number)[]) => {
            const selectedRows: T[] = [];
            selectedIds.forEach((id: string | number) => {
              const rowNode = this.gridApi?.getRowNode(id.toString());
              if (rowNode?.data) {
                selectedRows.push(rowNode.data);
              }
            });

            // Replace grid data with only selected rows
            this.gridApi?.setRowData(selectedRows);
          });
      } else {
        // Exiting edit mode: Restore original data
        this.cellChanges.clear();
        // Refresh grid to get all original data back
        this.search();
      }
    }
  }

  private isEditableField(field: string): boolean {
    // Only allow editing of specific fields
    const editableFields = [
      'vendorProperties.google.annotatedUser',
      'vendorProperties.google.annotatedLocation',
      'vendorProperties.google.annotatedAssetId',
      'vendorProperties.google.notes',
      'vendorProperties.google.orgUnitPath',
    ];
    return editableFields.includes(field);
  }

  /**
   * Set up MutationObserver to watch for column selector DOM changes.
   * This ensures icons are re-injected whenever AG Grid updates the column selector.
   */
  private setupColumnSelectorObserver(): void {
    // Disconnect existing observer if any
    if (this.columnSelectorObserver) {
      this.columnSelectorObserver.disconnect();
    }

    // Create a new MutationObserver
    this.columnSelectorObserver = new MutationObserver(() => {
      // Debounce icon injection by using requestAnimationFrame
      requestAnimationFrame(() => {
        this.injectColumnSelectorIcons();
      });
    });

    // Observe the entire AG Grid container for changes
    // We'll watch for any changes to the sidebar that might indicate column selector updates
    const observeTarget = () => {
      const agRoot = document.querySelector('.ag-root-wrapper');
      if (agRoot) {
        this.columnSelectorObserver?.observe(agRoot, {
          childList: true,
          subtree: true,
        });
      } else {
        // If not found, retry after a short delay
        setTimeout(observeTarget, 100);
      }
    };

    observeTarget();
  }

  /**
   * Dynamically inject icons into column selector labels.
   * This method directly manipulates the DOM to add Material Icon spans
   * next to columns that have a `columnSelectorIcon` property defined.
   */
  private injectColumnSelectorIcons(): void {
    if (!this.columnDefs) return;

    // Find columns with columnSelectorIcon
    const columnsWithIcons = this.columnDefs.filter(
      (col) => col.columnSelectorIcon && col.headerName
    );

    if (columnsWithIcons.length === 0) return;

    // Helper function to inject icons once DOM is ready
    const injectIcons = () => {
      const sidebar = document.querySelector('.ag-side-bar');
      if (!sidebar) {
        return false;
      }

      const columnItems = sidebar.querySelectorAll('.ag-column-select-column');

      // Check if we have the expected number of columns (more than just "Select All")
      if (columnItems.length < 2) {
        return false; // DOM not ready yet
      }

      columnsWithIcons.forEach((col) => {
        const headerName = col.headerName;
        const iconName = col.columnSelectorIcon;

        // Find the column item by matching the text content of the label
        let labelElement: HTMLElement | null = null;

        for (const item of Array.from(columnItems)) {
          const labelSpan = item.querySelector(
            '.ag-column-select-column-label'
          );
          const labelText = labelSpan?.textContent?.trim();

          if (labelSpan && labelText === headerName) {
            labelElement = labelSpan as HTMLElement;
            break;
          }
        }

        if (!labelElement) {
          return;
        }

        // Remove any existing icon first (in case of re-injection after setColumnDefs)
        const existingIcon = labelElement.querySelector(
          '.cdw-column-selector-icon'
        );
        if (existingIcon) {
          existingIcon.remove();
        }

        // Create Material Icon span
        const iconSpan = document.createElement('span');
        iconSpan.className = 'material-icons cdw-column-selector-icon';
        iconSpan.textContent = iconName ?? '';
        iconSpan.style.fontSize = '16px';
        iconSpan.style.marginLeft = '4px';
        iconSpan.style.paddingBottom = '2px';
        iconSpan.style.verticalAlign = 'middle';
        iconSpan.style.color = '#666';

        labelElement.appendChild(iconSpan);
      });

      return true;
    };

    // Retry logic with multiple requestAnimationFrame attempts
    const tryInject = (attempt = 1, maxAttempts = 10) => {
      requestAnimationFrame(() => {
        if (!injectIcons() && attempt < maxAttempts) {
          tryInject(attempt + 1, maxAttempts);
        }
      });
    };

    tryInject();
  }

  private hexToRgba(hex: string, opacity: number): string {
    // Remove # if present
    const cleanHex = hex.replace('#', '');

    // Parse hex to RGB
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  public onCellClicked(event: CellClickedEvent): void {
    // Disable all cell click interactions on rows that are updating
    const cellData = event.data as Record<string, unknown>;
    // biome-ignore lint/complexity/useLiteralKeys: bracket notation required by noPropertyAccessFromIndexSignature
    if (cellData?.['isUpdating']) {
      return;
    }

    // Special handling for preview icon column - trigger row navigation
    if (event.colDef.cellRenderer === 'previewIconComponent') {
      // Create a RowClickedEvent-like object to emit
      const rowClickedEvent = {
        ...event,
        data: event.data,
        node: event.node,
        rowIndex: event.rowIndex,
      } as RowClickedEvent<T>;
      this.rowClicked.emit(rowClickedEvent);
      return;
    }

    // Skip if clicking on checkbox or other non-data cells
    if (!event.colDef.field || event.colDef.cellRenderer) {
      return;
    }

    // Only enable cell-click editing if configured
    if (!this.enableCellClickEditing) {
      return;
    }

    // Check if the clicked cell is editable
    const isEditable = this.isEditableField(event.colDef.field);

    if (isEditable && !this.isEditMode) {
      // Check if an in-cell edit job is active before entering edit mode
      if (this.hasActiveInCellEditJob()) {
        const tooltipMessage =
          'In-cell editing is temporarily disabled while updates are being processed';
        this.showClickTooltip(event.event as MouseEvent, tooltipMessage);
        return;
      }
      // Enable edit mode when clicking an editable cell
      this.enterEditMode();
    } else if (!isEditable && this.showNonEditableTooltips) {
      // Show tooltip manually on click for non-editable cells
      const tooltipMessage = this.isEditMode
        ? 'Permission restricted. View only'
        : 'Permission restricted. Click an editable cell to enable edit mode.';

      // Create and show a custom tooltip element
      this.showClickTooltip(event.event as MouseEvent, tooltipMessage);
    }
  }

  private showClickTooltip(mouseEvent: MouseEvent, message: string): void {
    // Clean up any existing tooltip
    this.removeClickTooltip();

    // Get the target cell element
    const cellElement = (mouseEvent.target as HTMLElement).closest('.ag-cell');
    if (!cellElement) return;

    // Create tooltip element with AG Grid styling
    const tooltip = document.createElement('div');
    tooltip.className = 'cdw-click-tooltip ag-tooltip';
    tooltip.textContent = message;
    tooltip.style.position = 'fixed';
    tooltip.style.backgroundColor = '#f5f5f5';
    tooltip.style.color = '#000';
    tooltip.style.padding = '5px';
    tooltip.style.border = '1px solid #ddd';
    tooltip.style.borderRadius = '2px';
    tooltip.style.fontSize = '12px';
    tooltip.style.fontFamily = 'inherit';
    tooltip.style.zIndex = '10000';
    tooltip.style.pointerEvents = 'none';
    tooltip.style.whiteSpace = 'nowrap';
    tooltip.style.boxShadow = '0 1px 4px 1px rgba(0, 0, 0, 0.2)';
    tooltip.style.transition = 'opacity 0.2s';

    // Initial position
    this.positionTooltip(tooltip, mouseEvent.clientX, mouseEvent.clientY);

    // Add to body
    document.body.appendChild(tooltip);
    this.activeClickTooltip = tooltip;

    // Mouse move handler to follow cursor
    this.tooltipMouseMoveHandler = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      if (this.activeClickTooltip) {
        this.positionTooltip(
          this.activeClickTooltip,
          mouseEvent.clientX,
          mouseEvent.clientY
        );
      }
    };

    // Mouse leave handler to remove tooltip when leaving the cell
    this.tooltipMouseLeaveHandler = () => {
      this.removeClickTooltip();
    };

    // Attach listeners to the cell
    if (this.tooltipMouseMoveHandler) {
      cellElement.addEventListener('mousemove', this.tooltipMouseMoveHandler);
    }
    if (this.tooltipMouseLeaveHandler) {
      cellElement.addEventListener('mouseleave', this.tooltipMouseLeaveHandler);
    }
  }

  private positionTooltip(
    tooltip: HTMLElement,
    mouseX: number,
    mouseY: number
  ): void {
    // Position tooltip to the right and below the cursor
    tooltip.style.left = `${mouseX + 10}px`;
    tooltip.style.top = `${mouseY + 10}px`;
  }

  private removeClickTooltip(): void {
    // Remove tooltip element
    if (this.activeClickTooltip) {
      this.activeClickTooltip.remove();
      this.activeClickTooltip = null;
    }

    // Remove event listeners
    if (this.tooltipMouseMoveHandler || this.tooltipMouseLeaveHandler) {
      const cells = document.querySelectorAll('.ag-cell');
      cells.forEach((cell) => {
        if (this.tooltipMouseMoveHandler) {
          cell.removeEventListener('mousemove', this.tooltipMouseMoveHandler);
        }
        if (this.tooltipMouseLeaveHandler) {
          cell.removeEventListener('mouseleave', this.tooltipMouseLeaveHandler);
        }
      });
      this.tooltipMouseMoveHandler = null;
      this.tooltipMouseLeaveHandler = null;
    }
  }

  private initializeTooltips(): void {
    if (!this.gridApi) return;
    if (!this.showNonEditableTooltips) return;

    const columnDefs = this.gridApi.getColumnDefs();
    if (columnDefs) {
      columnDefs.forEach((colDef: ColDef) => {
        if (colDef.field) {
          const isEditable = this.isEditableField(colDef.field);

          // Disable sorting in edit mode, otherwise ensure all columns remain sortable
          colDef.sortable = this.isEditMode ? false : colDef.sortable !== false;

          if (!isEditable) {
            // Add tooltip for non-editable cells when NOT in edit mode
            colDef.tooltipValueGetter = () =>
              'Permission restricted. Click an editable cell to enable edit mode.';
          }
        }
      });
      this.gridApi.setColumnDefs(columnDefs);
    }
  }

  private enterEditMode(): void {
    if (this.isEditMode || !this.gridApi) return;

    this.isEditMode = true;
    this.editModeChanged.emit(true);

    const columnDefs = this.gridApi.getColumnDefs();
    if (columnDefs) {
      columnDefs.forEach((colDef: ColDef) => {
        if (colDef.field) {
          const isEditable = this.isEditableField(colDef.field);

          // Disable sorting for ALL columns in edit mode
          colDef.sortable = false;

          if (isEditable) {
            colDef.editable = () => {
              // Disable all editing if an in-cell edit job is in progress
              return !this.hasActiveInCellEditJob();
            };

            // Add tooltip for disabled cells during in-cell edit job processing
            colDef.tooltipValueGetter = () => {
              if (this.hasActiveInCellEditJob()) {
                return 'Cell editing is temporarily disabled while updates are being processed';
              }
              return undefined;
            };
            // Add visual indicator for editable cells - use function to check if modified or saving
            colDef.cellClass = (params) => {
              const rowId = params.data?.[this.filterBySelectedColumnName];
              const field = params.colDef.field;

              // Check if cell is currently saving
              if (rowId && field && this.isCellSaving(rowId, field)) {
                return 'saving-cell';
              }

              // Check if cell has been modified
              if (rowId && field && this.cellChanges.has(String(rowId))) {
                const changes = this.cellChanges.get(String(rowId));
                if (changes && changes[field] !== undefined) {
                  return 'modified-cell';
                }
              }
              return 'editable-cell';
            };
            // Remove tooltip from editable cells
            colDef.tooltipValueGetter = undefined;
          } else if (this.showNonEditableTooltips) {
            // Add tooltip for non-editable cells when in edit mode (only if enabled)
            colDef.tooltipValueGetter = () =>
              'Permission restricted. View only';
          }
        }
      });
      this.gridApi.setColumnDefs(columnDefs);
      // Force refresh to apply tooltip changes
      this.gridApi.refreshCells({ force: true });

      // Clear any previous changes when entering edit mode
      this.cellChanges.clear();
    }
  }

  public onCellEditingStarted(event: CellEditingStartedEvent): void {
    if (!this.isEditMode) return;

    const deviceId = event.data.id;
    const field = event.colDef.field;

    if (!field) return;

    // Get the original value
    const originalValue = event.data[field];

    const cellEditorElement = event.api.getFocusedCell();
    if (cellEditorElement) {
      const inputElement = document.activeElement as HTMLInputElement;

      if (inputElement && inputElement.tagName === 'INPUT') {
        // Listen for any input event (typing)
        const inputHandler = () => {
          const currentValue = inputElement.value;

          // Only mark as changed if the value is different from original
          if (currentValue !== originalValue) {
            if (!this.cellChanges.has(deviceId)) {
              this.cellChanges.set(deviceId, {});
            }

            // Store the change
            const deviceChanges = this.cellChanges.get(deviceId);
            if (deviceChanges) {
              deviceChanges[field] = currentValue;
            }
          } else {
            // Value is back to original, remove the change
            if (this.cellChanges.has(deviceId)) {
              const deviceChanges = this.cellChanges.get(deviceId);
              if (deviceChanges) {
                delete deviceChanges[field];

                // If no more changes for this device, remove the entry
                if (Object.keys(deviceChanges).length === 0) {
                  this.cellChanges.delete(deviceId);
                }
              }
            }
          }

          // Refresh the cell styling to reflect the change
          if (this.gridApi) {
            const rowNode = this.gridApi.getRowNode(String(deviceId));
            if (rowNode) {
              this.gridApi.refreshCells({
                rowNodes: [rowNode],
                columns: [field],
                force: true,
              });
            }
          }
        };

        inputElement.addEventListener('input', inputHandler, { once: false });

        // Clean up listener when editing stops
        inputElement.addEventListener(
          'blur',
          () => {
            inputElement.removeEventListener('input', inputHandler);
          },
          { once: true }
        );
      }
    }
  }

  public onCellValueChanged(event: CellValueChangedEvent): void {
    if (!this.isEditMode) return;

    const deviceId = event.data.id;
    const field = event.colDef.field;
    const newValue = event.newValue;

    if (!field) return;

    if (!this.cellChanges.has(deviceId)) {
      this.cellChanges.set(deviceId, {});
    }

    const changes = this.cellChanges.get(deviceId);
    if (changes) {
      changes[field] = newValue;
    }

    // Refresh the cell styling to reflect the change
    if (this.gridApi && event.node) {
      this.gridApi.refreshCells({
        rowNodes: [event.node],
        columns: [field],
        force: true,
      });
    }
  }

  public saveChanges(): void {
    if (this.cellChanges.size === 0) {
      return;
    }
    // Emit event to parent component to handle the save logic
    this.saveChangesRequested.emit();
  }

  public getCellChanges(): Map<string, Record<string, unknown>> {
    return this.cellChanges;
  }

  /**
   * Mark specific cells as saving (shows loading indicator)
   * Sets the active in-cell edit job - disables all editing while active
   * @param updates - Array of updates with deviceId and field changes
   * @param jobId - In-cell edit job ID to track (NOT bulk update or other job types)
   */
  public markCellsAsSaving(
    updates: Record<string, unknown>[],
    jobId: number
  ): void {
    // Set the active in-cell edit job
    this.activeInCellEditJobId = jobId;

    // Add cells to saving set
    updates.forEach((update) => {
      const deviceId = update[this.filterBySelectedColumnName] as
        | string
        | number;
      if (!deviceId) return;

      Object.keys(update).forEach((field) => {
        if (field !== this.filterBySelectedColumnName) {
          const cellKey = `${deviceId}-${field}`;
          this.savingCells.add(cellKey);
        }
      });
    });

    // Refresh cells to update UI
    this.refreshCellsForSavingState();
  }

  /**
   * Clear saving state for the in-cell edit job (removes loading indicator)
   * Re-enables editing when the job is complete
   * @param jobId - In-cell edit job ID to clear (NOT bulk update or other job types)
   */
  public clearSavingCells(jobId: number): void {
    // Only clear if this is the active in-cell edit job
    if (this.activeInCellEditJobId === jobId) {
      this.activeInCellEditJobId = null;
      this.savingCells.clear();
    }

    // Refresh all cells to update loading state and re-enable editing
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  /**
   * Check if an in-cell edit job is currently active
   * Only tracks the job explicitly passed via exitEditMode or markCellsAsSaving
   * Does NOT track bulk update jobs or other job types
   * @returns true if there is an in-cell edit job being processed
   */
  private hasActiveInCellEditJob(): boolean {
    return this.activeInCellEditJobId !== null;
  }

  /**
   * Check if a specific cell is currently saving
   */
  private isCellSaving(deviceId: string | number, field: string): boolean {
    const cellKey = `${deviceId}-${field}`;
    return this.savingCells.has(cellKey);
  }

  /**
   * Check if any cells in a row are currently saving
   */
  private isRowSaving(deviceId: string | number): boolean {
    for (const cellKey of this.savingCells) {
      if (cellKey.startsWith(`${deviceId}-`)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Helper method to extract device ID from row data
   */
  private getRowId(data: unknown): string | number | null {
    if (!data) return null;
    const row = data as T;
    const id = row[this.filterBySelectedColumnName as keyof T];
    return (id as string | number) ?? null;
  }

  /**
   * Refresh cells that are in saving state
   */
  private refreshCellsForSavingState(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.refreshCells({ force: true });
  }

  /**
   * Exit edit mode with optional grid refresh
   * @param refreshGrid - If true, refreshes grid data by calling search()
   * @param savingCells - Optional cells to mark as saving
   */
  public exitEditMode(
    refreshGrid = true,
    savingCellsData?: { updates: Record<string, unknown>[]; jobId: number }
  ): void {
    this.cellChanges.clear();

    // CRITICAL: If we have cells to mark as saving, populate BEFORE setColumnDefs
    // This ensures cellClass functions can find the cells when AG Grid evaluates them
    if (savingCellsData) {
      // Set the active in-cell edit job
      this.activeInCellEditJobId = savingCellsData.jobId;

      // Add cells to saving set
      savingCellsData.updates.forEach((update) => {
        const deviceId = update[this.filterBySelectedColumnName] as
          | string
          | number;
        if (!deviceId) return;

        const fields = Object.keys(update);
        fields.forEach((field) => {
          if (field !== this.filterBySelectedColumnName) {
            const cellKey = `${deviceId}-${field}`;
            this.savingCells.add(cellKey);
          }
        });
      });
    }

    // Reset column defs to non-editable
    if (this.gridApi) {
      // Use the original columnDefs array to preserve all custom properties
      const originalColumnDefs = this.columnDefs;
      originalColumnDefs.forEach((colDef: ColDef) => {
        if (colDef.field) {
          const isEditable = this.isEditableField(colDef.field);

          if (isEditable) {
            colDef.editable = false;
            // Re-enable sorting when exiting edit mode
            colDef.sortable = colDef.sortable !== false;

            // Keep cellClass function to show saving indicators even after exiting edit mode
            colDef.cellClass = (params) => {
              const rowId = params.data?.[this.filterBySelectedColumnName];
              const field = params.colDef.field;

              // Check if cell is currently saving
              if (rowId && field && this.isCellSaving(rowId, field)) {
                return 'saving-cell';
              }

              return '';
            };

            // Remove tooltips from editable cells
            colDef.tooltipValueGetter = undefined;
          } else if (this.showNonEditableTooltips) {
            // Add tooltip for non-editable cells when NOT in edit mode (only if enabled)
            colDef.tooltipValueGetter = () =>
              'Permission restricted. Click an editable cell to enable edit mode.';
          }
        }

        // Restore checkbox column visibility
        if (colDef.cellRenderer === 'selectCheckboxComponent') {
          colDef.hide = false;
        }
      });
      this.gridApi.setColumnDefs(originalColumnDefs);

      // Refresh cells based on context:
      // 1. If refreshGrid=true: full grid refresh
      // 2. If savingCellsData provided: refresh only those specific cells
      // 3. Otherwise: no refresh (caller will handle it)
      if (refreshGrid) {
        this.gridApi.refreshCells({ force: true });
      } else if (savingCellsData) {
        // Refresh only the cells we just marked as saving
        this.refreshCellsForSavingState();
      }
      // MutationObserver will automatically re-inject icons when DOM updates
    }

    this.isEditMode = false;
    this.editModeChanged.emit(false);
    this.isSaving = false;

    // Optionally restore original grid data
    if (refreshGrid) {
      this.search();
    }
  }

  public setIsSaving(saving: boolean): void {
    this.isSaving = saving;
  }

  public revertChanges(): void {
    // If there are pending changes, emit event to parent to show confirmation
    if (this.cellChanges.size > 0) {
      this.discardChangesRequested.emit();
      return;
    }

    // No changes, just exit edit mode
    Promise.resolve().then(() => {
      this.performRevertChanges();
    });
  }

  public performRevertChanges(): void {
    // When reverting changes, we should exit edit mode completely
    // This will restore the original grid data via search() in exitEditMode()
    if (this.isEditMode) {
      this.exitEditMode();
    }
  }

  public search(
    options: { filterBySelected?: boolean; clearFilterBySelected?: boolean } = {
      filterBySelected: false,
      clearFilterBySelected: false,
    }
  ): void {
    const {
      filterBySelected,
      clearFilterBySelected,
    }: { filterBySelected?: boolean; clearFilterBySelected?: boolean } =
      options;

    if (filterBySelected && this.searchGridDataSource === 'sql') {
      this.componentState.clearSearch();
      this.componentState.setHeadlessFilterFields({
        filterBySelectedColumnName: this.filterBySelectedColumnName,
      });
    }
    if (filterBySelected && this.searchGridDataSource === 'api') {
      this.componentState.clearNextPageToken();
      this.componentState.apiSearchFilterBySelected();
    }
    if (clearFilterBySelected && this.searchGridDataSource === 'api') {
      this.componentState.clearNextPageToken();
      this.componentState.clearSearch();
      this.componentState.setSearchItemTotal({ searchItemTotal: -1 });
    }
    if (clearFilterBySelected && this.searchGridDataSource === 'sql') {
      this.componentState.clearHeadlessFilterFields();
    }

    // For client-side model, load data directly
    if (this.gridOptions.rowModelType === 'clientSide') {
      this.loadClientSideData();
    }
    this.clearError.emit();
  }

  private loadClientSideData(): void {
    if (
      this.searchGridDataSource === 'api' &&
      this.cdwSearchGridDataService.fetchApiData
    ) {
      this.selectedFilterFields$
        .pipe(take(1))
        .subscribe((selectedFilterFields: SelectedFilterField[]) => {
          this.cdwSearchGridDataService
            .fetchApiData?.({
              activeFilters: selectedFilterFields as SelectedFilterField[],
              maxResults: 1000, // Load all data for client-side
            })
            .subscribe({
              next: (response: ApiResponse<T>) => {
                this.gridApi?.setRowData(response.data);
                this.componentState.setSearchItemTotal({
                  searchItemTotal: response.data.length,
                });
              },
              error: (error) => {
                this.errorRetrievingData.emit(this.searchError);
              },
            });
        });
    } else if (
      this.searchGridDataSource === 'sql' &&
      this.cdwSearchGridDataService.fetchSqlData
    ) {
      this.selectedFilterFields$
        .pipe(take(1))
        .subscribe((selectedFilterFields: SelectedFilterField[]) => {
          this.cdwSearchGridDataService
            .fetchSqlData?.({
              activeFilters: selectedFilterFields as SelectedFilterField[],
              supplementalFilters: [],
              startRow: 0,
              endRow: 1000, // Load all data for client-side
            })
            .subscribe({
              next: (response: SqlResponse<T>) => {
                this.gridApi?.setRowData(response.data);
                this.componentState.setSearchItemTotal({
                  searchItemTotal: response.itemTotal,
                });

                // Emit dataLoaded event after data is set
                this.dataLoaded.emit();

                // Restore pagination after data is loaded (for client-side model)
                this.initialPageLoad$
                  .pipe(take(1))
                  .subscribe((initialPageLoad: boolean) => {
                    if (initialPageLoad) {
                      this.currentPage$
                        .pipe(take(1))
                        .subscribe((currentPage: number) => {
                          this.gridApi?.paginationGoToPage(currentPage);
                          this.componentState.setInitialPageLoad({
                            initialPageLoad: false,
                          });
                        });
                    }
                  });
              },
              error: (error) => {
                this.errorRetrievingData.emit(this.searchError);
              },
            });
        });
    }
  }

  /**
   * Get the current column order from the grid.
   * Returns columns in their current display order with visibility flag.
   */
  public getColumnOrder(): CdwGridColumn[] {
    const columnState = this.columnApi?.getColumnState() ?? [];
    return columnState.map((col) => ({
      name: col.colId,
      hide: col.hide ?? false,
    }));
  }

  /**
   * Get the current sort order from the grid.
   * Returns sorted columns in sort priority order.
   */
  public getSortOrder(): CdwGridSort[] {
    const columnState = this.columnApi?.getColumnState() ?? [];
    return columnState
      .filter((col) => col.sort)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((col) => ({
        name: col.colId,
        direction: col.sort?.toUpperCase() as 'ASC' | 'DESC',
      }));
  }

  /**
   * Get the total item count for the current query (all matching rows, not just current page).
   */
  public getItemTotal(): number {
    return this.gridApi?.paginationGetRowCount() ?? 0;
  }

  private updateColumnState(): void {
    if (this.columnApi && this.gridReady$.value) {
      const columnState = this.columnApi.getColumnState();
      this.componentState.setColumnState({ columnState });
    }
  }

  private updateSortOrder(): void {
    if (this.columnApi) {
      const sortLookup: Record<
        number,
        { name: string; direction?: 'ASC' | 'DESC' }
      > = this.columnApi.getColumnState().reduce(
        (
          accumulator: Record<
            number,
            { name: string; direction?: 'ASC' | 'DESC' }
          >,
          columnState: ColumnState
        ) => {
          if (columnState?.sort) {
            const key = columnState.sortIndex ?? 0;
            accumulator[key] = {
              name: columnState.colId,
              direction: columnState.sort?.toUpperCase() as
                | 'ASC'
                | 'DESC'
                | undefined,
            };
          }
          return accumulator;
        },
        {} as Record<number, { name: string; direction?: 'ASC' | 'DESC' }>
      );
      const sortOrder: { name: string; direction?: 'ASC' | 'DESC' }[] = Array(
        Object.keys(sortLookup).length
      ).map((_, key) => sortLookup[key]);
      this.componentState.setSortOrder({ sortOrder });
    }
  }

  /**
   * Mark the first data column (after locked columns) with a CSS class for styling.
   * This allows the firstColumnTextColor feature to work correctly.
   */
  private markFirstDataColumn(): void {
    // Find the first column that is NOT a locked column
    const firstDataColumnIndex = this.columnDefs.findIndex(
      (col) =>
        col.colId !== 'selectCheckboxComponent' &&
        col.colId !== 'previewIconComponent' &&
        col.cellRenderer !== 'selectCheckboxComponent' &&
        col.cellRenderer !== 'previewIconComponent'
    );

    if (firstDataColumnIndex >= 0) {
      // Remove the class from all columns first
      this.columnDefs.forEach((col) => {
        if (col.cellClass) {
          if (typeof col.cellClass === 'string') {
            col.cellClass = col.cellClass
              .replace('first-data-column', '')
              .trim();
          }
        }
      });

      // Add the class to the first data column
      const firstDataColumn = this.columnDefs[firstDataColumnIndex];
      if (firstDataColumn) {
        if (!firstDataColumn.cellClass) {
          firstDataColumn.cellClass = 'first-data-column';
        } else if (typeof firstDataColumn.cellClass === 'string') {
          if (!firstDataColumn.cellClass.includes('first-data-column')) {
            firstDataColumn.cellClass += ' first-data-column';
          }
        }
      }
    }
  }

  private getGridOptions(): GridOptions {
    const gridOptions: GridOptions = {
      // Default AG Grid options
      suppressLoadingOverlay: true,
      pagination: true,
      // rowSelection: 'single',
      rowSelection: 'multiple',
      suppressRowClickSelection: true,
      animateRows: true,
      rowModelType: 'clientSide',
      paginationPageSize: 50,
      overlayNoRowsTemplate: `<span>${this.noRowsOverlayText}</span>`,
      tooltipShowDelay: this.showNonEditableTooltips ? 999999 : 0, // Disable hover tooltips when using click tooltips
      tooltipHideDelay: 2000, // Keep tooltips visible for 2 seconds after mouse leaves
      tooltipMouseTrack: !this.showNonEditableTooltips, // Disable mouse tracking when using click tooltips
      suppressMovableColumns: !this.enableColumnReordering, // Control column drag-and-drop reordering
      enableRangeSelection: true, // Enable cell range selection - single click to focus, Ctrl+C to copy
      processCellForClipboard: (params) => params?.value ?? '', // Process cell values for clipboard
      // Only preserve pagination/selections when initialPage is provided (for device/audit log summary navigation)
      deltaRowDataMode: this.initialPage !== undefined, // Efficiently update only changed rows, preserving pagination/selections
      suppressScrollOnNewData: this.initialPage !== undefined, // Prevent scroll jump when data updates
      context: {
        useMatIcon: this.useMatIcon,
        customTextColor: this.customTextColor,
        firstColumnTextColor: this.firstColumnTextColor,
        previewField: this.previewField,
        previewFieldLabel: this.previewFieldLabel,
      },
      components: {
        multiSelectHeaderComponent: MultiSelectHeaderComponent<{ id: string }>,
        selectCheckboxComponent: SelectCheckboxComponent<{ id: string }>,
        deleteItemIconComponent: DeleteItemIconComponent<T>,
        previewIconComponent: PreviewIconComponent<T>,
        customHeaderComponent: CustomHeaderComponent,
      },
      getRowId: (params: GetRowIdParams<T>) => params.data.id.toString(),
      getRowClass: (params: RowClassParams<T>) => {
        const data = params.data as Record<string, unknown>;
        // biome-ignore lint/complexity/useLiteralKeys: bracket notation required by noPropertyAccessFromIndexSignature
        return (data?.['isStale'] || data?.['isUpdating']) ? 'stale-row' : undefined;
      },
      onCellEditingStarted: (event: CellEditingStartedEvent) =>
        this.onCellEditingStarted(event),
      onCellValueChanged: (event: CellValueChangedEvent) =>
        this.onCellValueChanged(event),
      onCellClicked: (event: CellClickedEvent) => this.onCellClicked(event),
      onColumnMoved: () => this.updateColumnState(),
      onColumnResized: () => this.updateColumnState(),
      onColumnVisible: () => this.updateColumnState(),
      onDragStopped: () => this.updateColumnState(),
      singleClickEdit: true, // Enable single-click editing
      stopEditingWhenCellsLoseFocus: true, // Auto-save when clicking away
      // External filter for "Show Selected Only" feature
      isExternalFilterPresent: this.isExternalFilterPresent,
      doesExternalFilterPass: this.doesExternalFilterPass,
    };

    // Apply custom row height if provided
    if (this.rowHeight) {
      gridOptions.rowHeight = this.rowHeight;
    }

    return gridOptions;
  }

}
